
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/DataManagement/DataTable';
import { OrganizationMaterialsManager } from '@/components/DataManagement/OrganizationMaterialsManager';
import { OrganizationMetricsManager } from '@/components/DataManagement/OrganizationMetricsManager';
import { QuickEntryForm } from '@/components/DataManagement/QuickEntryForm';
import { DataUploadForm } from '@/components/DataManagement/DataUploadForm';
import { MetricsLibrary } from '@/components/DataManagement/MetricsLibrary';
import { MaterialsLibrary } from '@/components/DataManagement/MaterialsLibrary';
import { EnhancedTagManagementSystem } from '@/components/Dashboard/EnhancedTagManagementSystem';
import {
  BarChart3,
  Package,
  Plus,
  Upload,
  Database,
  Library,
  Tag,
  Menu
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

const DataManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const tabs = [
    { value: 'overview', label: 'Overview', icon: BarChart3 },
    { value: 'quick-entry', label: 'Quick Entry', icon: Plus },
    { value: 'upload', label: 'Upload', icon: Upload },
    { value: 'metrics', label: 'Metrics', icon: Database },
    { value: 'materials', label: 'Materials', icon: Package },
    { value: 'library', label: 'Library', icon: Library },
    { value: 'tags', label: 'Tags', icon: Tag },
  ];

  const activeTabLabel = tabs.find(t => t.value === activeTab)?.label || 'Overview';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Data Management</h1>
          <p className="text-gray-600 mt-2">
            Manage your organization's ESG data, metrics, and materials with comprehensive tools and analytics.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Desktop Tabs List */}
        <TabsList className="hidden md:grid w-full grid-cols-7">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2">
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Mobile Tabs Dropdown */}
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full flex justify-start items-center gap-3 px-4 py-6">
                <Menu className="h-5 w-5 text-gray-500" />
                <span className="flex items-center gap-2 border-l pl-3">
                  {React.createElement(tabs.find(t => t.value === activeTab)?.icon || BarChart3, { className: "h-5 w-5" })}
                  <span className="font-semibold">{activeTabLabel}</span>
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64" align="start">
              {tabs.map((tab) => (
                <DropdownMenuItem
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className="flex items-center gap-2 py-3"
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <TabsContent value="overview" className="space-y-6">
          {/* Organizational Data Overview */}
          <Card>
            <CardHeader>
              <CardTitle>Organizational Data Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable onTabChange={setActiveTab} />
            </CardContent>
          </Card>

          {/* Organization Materials Management */}
          <OrganizationMaterialsManager onTabChange={setActiveTab} />

          {/* Organization Metrics Management */}
          <OrganizationMetricsManager onTabChange={setActiveTab} />
        </TabsContent>

        <TabsContent value="quick-entry" className="space-y-4">
          <QuickEntryForm onSuccess={() => {
            // Refresh data if needed
            console.log('Data added successfully');
          }} />
        </TabsContent>

        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upload Data</CardTitle>
            </CardHeader>
            <CardContent>
              <DataUploadForm />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Metrics Library</CardTitle>
            </CardHeader>
            <CardContent>
              <MetricsLibrary />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="materials" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Materials Library</CardTitle>
            </CardHeader>
            <CardContent>
              <MaterialsLibrary />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="library" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Unified Library</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5" />
                      Metrics Library
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MetricsLibrary />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Materials Library
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MaterialsLibrary />
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tags" className="space-y-4">
          <EnhancedTagManagementSystem />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DataManagement;
